import React, { useState, useEffect } from "react";
import { useHistory, Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../context/authContext";
import { obtenerDatosRecuperacion, actualizarPasswordRecuperacion } from "../../services/api";
import "./register.css";

export default function Register() {
  const history = useHistory();
  const location = useLocation();
  const { register, loading, logout } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const recoveryToken = queryParams.get("token");

  const [form, setForm] = useState({
    nombreUsuario: "",
    correo: "",
    password: "",
    confirm: ""
  });

  const [errors, setErrors] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    async function loadRecoveryData() {
      if (!recoveryToken) return;

      try {
        const data = await obtenerDatosRecuperacion(recoveryToken);

        setForm((prev) => ({
          ...prev,
          nombreUsuario: data?.usuario?.nombreUsuario || "",
          correo: data?.usuario?.correo || "",
          password: "",
          confirm: ""
        }));

        toast.success("Se cargó la información de la cuenta. Ingresa una nueva contraseña.");
      } catch (err) {
        toast.error(
          err?.response?.data?.mensaje ||
          "El enlace de recuperación no es válido o ya expiró."
        );
      }
    }

    loadRecoveryData();
  }, [recoveryToken]);

  function onChange(e) {
    const { name, value } = e.target;

    setForm((f) => ({ ...f, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: ""
    }));
  }

  function validate() {
    const nextErrors = {};

    if (!recoveryToken) {
      if (!form.nombreUsuario.trim()) {
        nextErrors.nombreUsuario = "Ingresa un nombre de usuario.";
      }

      if (!form.correo.trim()) {
        nextErrors.correo = "Ingresa un correo electrónico.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo)) {
        nextErrors.correo = "Escribe un correo válido.";
      }
    }

    if (!form.password) {
      nextErrors.password = "Ingresa una contraseña.";
    } else if (form.password.length < 6) {
      nextErrors.password = "La contraseña debe tener al menos 6 caracteres.";
    }

    if (!form.confirm) {
      nextErrors.confirm = "Confirma tu contraseña.";
    } else if (form.password !== form.confirm) {
      nextErrors.confirm = "Las contraseñas no coinciden.";
    }

    return nextErrors;
  }

  async function onSubmit(e) {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus("loading");

    try {
      if (recoveryToken) {
        await actualizarPasswordRecuperacion({
          token: recoveryToken,
          password: form.password
        });

        history.push("/login", {
          registroExitoso: true,
          mensaje: "La contraseña se actualizó correctamente. Ahora inicia sesión."
        });
      } else {
        await register({
          nombreUsuario: form.nombreUsuario.trim(),
          correo: form.correo.trim(),
          password: form.password
        });

        history.push("/login", {
          registroExitoso: true,
          mensaje: "Cuenta creada correctamente. Ahora inicia sesión."
        });
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.mensaje ||
        e?.message ||
        "No se pudo completar la operación. Intenta nuevamente."
      );
    } finally {
      setStatus("idle");
      logout();
    }
  }

  return (
    <div className="auth-wrap container">
      <div className="auth-card">
        <h1 className="auth-title">
          {recoveryToken ? "Restablecer contraseña" : "Crear cuenta"}
        </h1>

        <p className="auth-subtitle">
          {recoveryToken
            ? "Se cargó la información de la cuenta. Solo es necesario ingresar la nueva contraseña."
            : "Regístrate para guardar favoritos, alertas y ver tus recomendaciones."}
        </p>

        <form onSubmit={onSubmit} className="auth-form" noValidate>
          <label className="auth-label">
            Nombre de usuario
            <input
              className={`auth-input ${errors.nombreUsuario ? "input-error" : ""}`}
              name="nombreUsuario"
              value={form.nombreUsuario}
              onChange={onChange}
              placeholder="Tu nombre"
              autoComplete="username"
            />
            {errors.nombreUsuario && (
              <span className="field-error">{errors.nombreUsuario}</span>
            )}
          </label>

          <label className="auth-label">
            Correo
            <input
              className={`auth-input ${errors.correo ? "input-error" : ""}`}
              name="correo"
              type="email"
              value={form.correo}
              onChange={onChange}
              placeholder="tucorreo@dominio.com"
              autoComplete="email"
              disabled={!!recoveryToken}
            />
            {errors.correo && (
              <span className="field-error">{errors.correo}</span>
            )}
          </label>

          <label className="auth-label">
            Contraseña
            <div className="pwd">
              <input
                className={`auth-input ${errors.password ? "input-error" : ""}`}
                name="password"
                type={showPwd ? "text" : "password"}
                value={form.password}
                onChange={onChange}
                placeholder={recoveryToken ? "Escribe tu nueva contraseña" : "Mínimo 6 caracteres"}
                autoComplete="new-password"
              />

              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd((s) => !s)}
                aria-label={showPwd ? "Ocultar" : "Mostrar"}
              >
                {showPwd ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            {errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
          </label>

          <label className="auth-label">
            Confirmar contraseña
            <input
              className={`auth-input ${errors.confirm ? "input-error" : ""}`}
              name="confirm"
              type={showPwd ? "text" : "password"}
              value={form.confirm}
              onChange={onChange}
              placeholder="Repite tu contraseña"
              autoComplete="new-password"
            />
            {errors.confirm && (
              <span className="field-error">{errors.confirm}</span>
            )}
          </label>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={status === "loading" || loading}
          >
            {status === "loading" || loading
              ? (recoveryToken ? "Actualizando..." : "Creando cuenta…")
              : (recoveryToken ? "Guardar nueva contraseña" : "Crear cuenta")}
          </button>
        </form>

        {!recoveryToken && (
          <div className="auth-footnote">
            ¿Ya tienes una cuenta?{" "}
            <Link to="/login" className="auth-link">
              Inicia sesión
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}